import classnames from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import DropZone from 'es6/components/apps/sentence-builder/drop-zone';

/**
 * Helper method to determine if the drag event is on the left
 * or right side of the component.
 *
 * @param {HTMLElement} domNode    HTML element to find position relative to
 * @param {Event} dragEvent
 * @return {Boolean}
 */
function isLeftSide(domNode, dragEvent) {
  // Get the offset of the mouse event relative to the target.
  const rect    = domNode.getBoundingClientRect();
  const offsetX = dragEvent.clientX - rect.left;

  // Get the position of the middle of the target,
  // without taking padding into account.
  const style = window.getComputedStyle(domNode);
  const leftPadding = parseInt(style.paddingLeft, 10);
  const rightPadding = parseInt(style.paddingRight, 10);
  const widthWithoutPadding = domNode.offsetWidth - leftPadding - rightPadding;
  const offsetMiddle = leftPadding + (widthWithoutPadding / 2);

  // Now just return if the offset of X is greater than the
  // offset of the middle.
  return offsetX <= offsetMiddle;
}

const INITIAL_STATE = {
  shiftLeftSide: false,
};

/**
 * ShiftZone is similar to a drop zone, except it will determine
 * if the user is dropping a word piece on the left or right side
 * of the shift zone.
 *
 * It also provides a onDropLeft and onDropRight callback.
 */
export class ShiftZone extends DropZone {
  /**
   * @override
   * @returns {Object}
   */
  _getInitialState() {
    const state = super._getInitialState();
    return Object.assign(state, INITIAL_STATE);
  }

  /**
   * Extends the classnames to include
   *
   * @override
   * @returns {Object}
   */
  _getContainerProps() {
    const { shiftLeftSide, draggedOver } = this.state;
    const props = super._getContainerProps();

    props.className = classnames(props.className, 'shift-zone', {
      'shift-left-side': draggedOver && shiftLeftSide,
      'shift-right-side': draggedOver && !shiftLeftSide,
    });

    if (draggedOver && shiftLeftSide) {
      props['data-shift-left-side'] = true;
    }

    if (draggedOver && !shiftLeftSide) {
      props['data-shift-right-side'] = true;
    }

    return props;
  }

  /**
   * DOMNode is only accessible within the context of the component.
   * Allow the drag and drop methods to be extended after local methods have run.
   *
   * @override
   * @param {Event} e
   */
  _handleDragOver(e) {
    const shiftLeftSide = isLeftSide(ReactDOM.findDOMNode(this), e);

    // Update the state with the new shift value.
    this.setState({ shiftLeftSide });

    if (shiftLeftSide) {
      this._executeHandler(this.props.onDragLeft, e);
    } else {
      this._executeHandler(this.props.onDragRight, e);
    }

    super._handleDragOver(e);
  }

  /**
   * Handle callback for dropping on the left and right side.
   *
   * @override
   */
  _handleDrop(e) {
    if (this.state.shiftLeftSide) {
      this._executeHandler(this.props.onDropLeft, e);
    } else {
      this._executeHandler(this.props.onDropRight, e);
    }

    super._handleDrop(e);
  }
}

ShiftZone.propTypes = Object.freeze({
  onDragLeft: React.PropTypes.func,
  onDragRight: React.PropTypes.func,
  onDropLeft: React.PropTypes.func,
  onDropRight: React.PropTypes.func,
});

export default ShiftZone;
